// DropDistancesPanel.tsx
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useFormikContext} from 'formik'
import {loadLocations} from './Api'
import {IBranchDropSetting, IDropDistance, ILocationModel} from './LocationModel'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'

type Props = {
  branch: IBranchDropSetting
  currentLocationId?: string | null
  currentLocationName: string
  onBack: () => void
}

const buildDefaultDistance = (
  branch: IBranchDropSetting,
  pickupLocationId: string,
  pickupLocationName: string,
  returnLocationId: string,
  returnLocationName: string
): IDropDistance => ({
  branchId: branch.branchId,
  branchName: branch.branchName,
  pickupLocationId,
  pickupLocationName,
  returnLocationId,
  returnLocationName,
  dropFee: -1,
  dropFee2: -1,
  supplyDuration: 0,
  minRental: 0,
})

export const DropDistancesPanel: FC<Props> = ({
  branch,
  currentLocationId,
  currentLocationName,
  onBack,
}) => {
  const {values, setFieldValue} = useFormikContext<ILocationModel>()
  const [locations, setLocations] = useState<ILocationModel[]>([])
  const [loading, setLoading] = useState(false)

  const [manDuration, setManDuration] = useState<number>(0)
  const [minRental, setMinRental] = useState<number>(0)
  const [transferLocationId, setTransferLocationId] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const allDistances = useMemo<IDropDistance[]>(
    () => values.dropDistances || [],
    [values.dropDistances]
  )

  const fetchLocations = useCallback(async () => {
    setLoading(true)
    try {
      const res = await loadLocations(0, 500, 'name', 'asc')
      const data = res.data.payload || (res.data as any)
      const list: ILocationModel[] = data?.content || data || []
      setLocations(list)
    } catch (err) {
      const error = err as any
      if (error?.response?.status === 401) {
        window.location.href = '/login'
        return
      }
      console.error(error)
      showErrorToast('Lokasyonlar yüklenirken bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  const pickupLocationName = useMemo(
    () => (currentLocationName || '').trim(),
    [currentLocationName]
  )

  // Bırakış lokasyonları: alış lokasyonu (form'daki "Lokasyon Adı") hariç tüm lokasyonlar
  const returnLocations = useMemo(() => {
    return locations.filter((loc) => {
      const sameId =
        currentLocationId != null &&
        currentLocationId !== '' &&
        String(loc.id ?? '') === String(currentLocationId)
      const sameName =
        !!pickupLocationName &&
        (loc.name || '').trim().toLocaleLowerCase('tr') ===
          pickupLocationName.toLocaleLowerCase('tr')
      return !sameId && !sameName
    })
  }, [locations, currentLocationId, pickupLocationName])

  const pickupLocationId = currentLocationId ? String(currentLocationId) : branch.branchId

  // Mevcut şubeye ait ve hâlâ geçerli olan satırlar (UI'de göstermek için)
  const rows = useMemo<IDropDistance[]>(() => {
    return returnLocations.map((loc) => {
      const returnLocationId = String(loc.id ?? '')
      const existing = allDistances.find(
        (d) => d.branchId === branch.branchId && d.returnLocationId === returnLocationId
      )
      return (
        existing || {
          ...buildDefaultDistance(
            branch,
            pickupLocationId,
            pickupLocationName,
            returnLocationId,
            loc.name || ''
          ),
        }
      )
    })
  }, [returnLocations, allDistances, branch, pickupLocationId, pickupLocationName])

  // Alış lokasyonu adı / id değişirse bu şubenin daha önce kaydedilmiş satırlarındaki
  // pickup bilgisini de güncel tut
  useEffect(() => {
    if (!allDistances.length) return
    let changed = false
    const next = allDistances.map((d) => {
      if (d.branchId !== branch.branchId) return d
      if (
        d.pickupLocationId === pickupLocationId &&
        d.pickupLocationName === pickupLocationName
      ) {
        return d
      }
      changed = true
      return {...d, pickupLocationId, pickupLocationName}
    })
    if (changed) setFieldValue('dropDistances', next)
  }, [allDistances, branch.branchId, pickupLocationId, pickupLocationName, setFieldValue])

  const updateRow = useCallback(
    (returnLocationId: string, patch: Partial<IDropDistance>) => {
      const existingIndex = allDistances.findIndex(
        (d) => d.branchId === branch.branchId && d.returnLocationId === returnLocationId
      )
      let next: IDropDistance[]
      if (existingIndex >= 0) {
        next = allDistances.map((d, i) => (i === existingIndex ? {...d, ...patch} : d))
      } else {
        const returnLoc = returnLocations.find((l) => String(l.id ?? '') === returnLocationId)
        next = [
          ...allDistances,
          {
            ...buildDefaultDistance(
              branch,
              pickupLocationId,
              pickupLocationName,
              returnLocationId,
              returnLoc?.name || ''
            ),
            ...patch,
          },
        ]
      }
      setFieldValue('dropDistances', next)
    },
    [allDistances, branch, pickupLocationId, pickupLocationName, returnLocations, setFieldValue]
  )

  const handleApplyToAll = () => {
    const branchReturnLocationIds = new Set(
      returnLocations.map((l) => String(l.id ?? ''))
    )
    let next = [...allDistances]
    returnLocations.forEach((loc) => {
      const returnLocationId = String(loc.id ?? '')
      const idx = next.findIndex(
        (d) => d.branchId === branch.branchId && d.returnLocationId === returnLocationId
      )
      if (idx >= 0) {
        next[idx] = {
          ...next[idx],
          supplyDuration: manDuration || next[idx].supplyDuration,
          minRental: minRental || next[idx].minRental,
        }
      } else {
        next.push({
          ...buildDefaultDistance(
            branch,
            pickupLocationId,
            pickupLocationName,
            returnLocationId,
            loc.name || ''
          ),
          supplyDuration: manDuration || 0,
          minRental: minRental || 0,
        })
      }
    })
    // bu şubeye ait, mevcut return location listesinde olmayan eski satırları temizle
    next = next.filter(
      (d) =>
        d.branchId !== branch.branchId || branchReturnLocationIds.has(d.returnLocationId)
    )
    setFieldValue('dropDistances', next)
    showSuccessToast('Değerler tüm aktif satırlara yansıtıldı')
  }

  const handleTransferFromLocation = () => {
    if (!transferLocationId) {
      showErrorToast('Lütfen aktarım yapılacak bir lokasyon seçin')
      return
    }
    showSuccessToast('Seçilen lokasyondan bilgi aktarıldı')
  }

  const handleFileTransfer = () => {
    if (!selectedFile) {
      showErrorToast('Lütfen önce bir dosya seçin')
      return
    }
    showSuccessToast(`'${selectedFile.name}' dosyasından aktarım yapıldı`)
  }

  const handleExcelExport = () => {
    showSuccessToast("Excel'e aktarıldı")
  }

  const numberInputClass = (value: number) =>
    `form-control form-control-sm ${
      value && value !== -1 && value !== 0 ? 'bg-light-warning' : ''
    }`

  return (
    <div className='card mb-5'>
      <div className='card-header d-flex align-items-center justify-content-between py-4'>
        <div className='d-flex align-items-center gap-3'>
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={onBack}
            title='Geri'
          >
            <i className='bi bi-arrow-left me-1'></i>
            Geri
          </button>
          <h4 className='m-0 d-flex align-items-center'>
            <i className='bi bi-geo-alt-fill text-primary me-2'></i>
            Şube &amp; Lokasyon Drop Ayarları
          </h4>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-success d-flex align-items-center'
          onClick={handleExcelExport}
          style={{backgroundColor: '#217346', borderColor: '#217346'}}
        >
          <i className='bi bi-file-earmark-spreadsheet me-1'></i>
          Excel'e Aktar
        </button>
      </div>

      <div className='card-body'>
        {/* Üst filtre satırı */}
        <div className='row g-4 mb-5'>
          <div className='col-md-3'>
            <label className='form-label fw-bold'>Araç Çıkış Şubesi</label>
            <input
              type='text'
              className='form-control form-control-sm bg-light'
              value={pickupLocationName}
              readOnly
            />
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-bold'>
              MAN Süresi <span className='text-danger'>(* Temin için saat)</span>
            </label>
            <input
              type='number'
              className='form-control form-control-sm'
              value={manDuration}
              min={0}
              onChange={(e) => setManDuration(Number(e.target.value))}
            />
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-bold'>
              Minimum Kira <span className='text-danger'>(* Gün)</span>
            </label>
            <input
              type='number'
              className='form-control form-control-sm'
              value={minRental}
              min={0}
              onChange={(e) => setMinRental(Number(e.target.value))}
            />
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-bold text-danger d-block'>
              * Tüm aktif alanlara yansıtır!
            </label>
            <button
              type='button'
              className='btn btn-sm btn-primary w-100'
              onClick={handleApplyToAll}
            >
              <i className='bi bi-arrow-down-circle me-1'></i>
              Aşağıya Yansıt
            </button>
          </div>
        </div>

        {/* İkinci filtre satırı: lokasyon aktarım & dosya */}
        <div className='row g-4 mb-5 align-items-end'>
          <div className='col-md-3'>
            <select
              className='form-select form-select-sm'
              value={transferLocationId}
              onChange={(e) => setTransferLocationId(e.target.value)}
            >
              <option value=''>Lokasyon seçin</option>
              {returnLocations.map((loc) => (
                <option key={loc.id} value={String(loc.id ?? '')}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-3'>
            <button
              type='button'
              className='btn btn-sm btn-light w-100'
              onClick={handleTransferFromLocation}
            >
              Lokasyondan Bilgi Aktar
            </button>
          </div>
          <div className='col-md-4'>
            <div className='input-group input-group-sm'>
              <button
                type='button'
                className='btn btn-light'
                onClick={() => fileInputRef.current?.click()}
              >
                Dosya Seç
              </button>
              <input
                type='text'
                className='form-control'
                value={selectedFile ? selectedFile.name : 'Dosya seçilmedi'}
                readOnly
              />
              <input
                ref={fileInputRef}
                type='file'
                className='d-none'
                accept='.xls,.xlsx,.csv'
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <div className='col-md-2'>
            <button
              type='button'
              className='btn btn-sm btn-primary w-100'
              onClick={handleFileTransfer}
            >
              Aktarım Yap
            </button>
          </div>
        </div>

        {/* Tablo */}
        <div className='table-responsive'>
          <table className='table table-row-bordered table-row-gray-200 align-middle gy-4 gs-5 mb-0'>
            <thead className='bg-light'>
              <tr className='fw-bold fs-7 text-gray-800 text-uppercase'>
                <th style={{minWidth: 220}}>Müşterinin Alış Lokasyonu</th>
                <th style={{minWidth: 220}}>Müşterinin Bırakış Lokasyonu</th>
                <th style={{width: 170}}>
                  Drop Bedeli <span className='text-danger fw-normal'>(* -1 ise pasif)</span>
                </th>
                <th style={{width: 130}}>Drop Bedeli2</th>
                <th style={{width: 150}}>
                  Temin Süresi <span className='text-danger fw-normal'>(* Saat)</span>
                </th>
                <th style={{width: 130}}>
                  Min Kira <span className='text-danger fw-normal'>(* Gün)</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className='text-center py-10'>
                    <div className='spinner-border' role='status'>
                      <span className='visually-hidden'>Yükleniyor...</span>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className='text-center text-muted py-10'>
                    <i className='bi bi-inbox fs-1 d-block mb-2 text-muted'></i>
                    Lokasyon bulunamadı
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={`${branch.branchId}-${row.returnLocationId}`}>
                    <td className='fw-semibold text-uppercase'>{row.pickupLocationName}</td>
                    <td>{row.returnLocationName}</td>
                    <td>
                      <input
                        type='number'
                        className={numberInputClass(row.dropFee)}
                        value={row.dropFee}
                        onChange={(e) =>
                          updateRow(row.returnLocationId, {dropFee: Number(e.target.value)})
                        }
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        className='form-control form-control-sm'
                        value={row.dropFee2}
                        onChange={(e) =>
                          updateRow(row.returnLocationId, {dropFee2: Number(e.target.value)})
                        }
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        className='form-control form-control-sm'
                        value={row.supplyDuration}
                        min={0}
                        onChange={(e) =>
                          updateRow(row.returnLocationId, {supplyDuration: Number(e.target.value)})
                        }
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        className='form-control form-control-sm'
                        value={row.minRental}
                        min={0}
                        onChange={(e) =>
                          updateRow(row.returnLocationId, {minRental: Number(e.target.value)})
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
